


import React, { useContext, useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { useNavigate } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';

import IntroSection from './IntroSection';
import SecondSection from '../../Shared/SecondSection/SecondSection';
import ThirdSection from '../../Shared/ThirdSection/ThirdSection';
import CheckHealthSteps from '../../Shared/CheckHealthSteps/CheckHealthSteps';
import ConcernsSection from '../../Shared/ConcernsSection/ConcernsSection';
import CurebaySolutions from '../../Shared/CurebaySolutions/CurebaySolutions';
import LoginToast from '../../MainLayout/ToastifyLogin';
import { LoginContext } from '../../MainLayout/MainLayout';
import { getSmartVitalData } from './SmartVitalApi';
import { usePatientData } from "../../hooks/usePatientData";
import { FeedbackModal, useServicePageFeedback } from '../Home/FeedbackSystem';

const SmartVitals = () => {
  const { openLogin } = useContext(LoginContext);
  const storedData1 = JSON.parse(localStorage.getItem("loginObj"));
  const userData = storedData1;
  const { showFeedback, closeFeedback } = useServicePageFeedback(userData?.code, 'Smart Vitals');

  const loginToastRef = useRef(null);
  const toastRef = useRef(null);
  const navigate = useNavigate();

  const [attrs, setAttrs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [report_count, setReportCount] = useState(null);
  const { patientData } = usePatientData();


  const endpointBase = process.env.REACT_APP_REPORTCOUNTURL;
  const countApiUrl = `${endpointBase}smartvital/reports/count`;

  /* ── Auth guard ── */
  const handleOpen = (e) => {
    if (!userData?.code) {
      e.preventDefault();
      openLogin();
      return;
    }
    navigate('/smart-vital-instr');
  };

  const handleSecondButton = () => {
  navigate("/aireports", {
    state: {
      tab: "smart_vitals", // 👈 change based on requirement
    },
  });
};

  /* ── "Check Our Other AI Tools" card routing ── */
  const handleCardClick = (item) => {
  const subheading = item?.subheading?.toLowerCase() || "";

  // VisionCare is live — open its page
  if (subheading === "visioncare") {
    navigate("/visioncare");
    return;
  }

  if (subheading === "caresathi") {
    navigate("/caresathi");
  } else if (subheading === "skincare") {
    navigate("/skincare");
  } else if (subheading === "smartvitals") {
    navigate("/smartvital");
  } else if (subheading === "oralcare") {
    navigate("/oralcare");
  } else if (subheading === "lungscare") {
    navigate("/lungscare");
  } else if (subheading === "smart report") {
    navigate("/smartreport");
  } else if (subheading === "symptomchecker") {
    navigate("/symptomchecker");
  } else if (subheading === "smart diet") {
    navigate("/smartdiet");
  } else {
    console.warn("No route found for:", subheading);
  }
};

  /* ── Fetch CMS data ── */
  useEffect(() => {
    let isMounted = true;
    getSmartVitalData()
      .then((res) => {
        if (!isMounted) return;
        const entry = res.data?.data?.[0]?.attributes;
        setAttrs(entry || {});
      })
      .catch((err) => {
        if (!isMounted) return;
        setError(err);
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => { isMounted = false; };
  }, []);

  /* ── Fetch report count ── */
  useEffect(() => {
    if (!countApiUrl) return;
    const fetchCount = async () => {
      try {
        const res = await fetch(countApiUrl);
        const data = await res.json();
        setReportCount(data.report_count ?? null);
      } catch (err) {
        console.error('Failed to fetch report count:', err);
      }
    };
    fetchCount();
  }, [countApiUrl]);

  if (loading) return <div className="p-8 text-center">Loading…</div>;
  if (error)   return <div className="p-8 text-center text-red-600">Error loading data.</div>;
  if (!attrs)  return <div className="p-8 text-center">No data</div>;

  /* ── Map CMS attrs to shared-component props ── */
  const firstcompItem  = attrs.first?.[0]  || {};
  const firstSectionItems = [
    {
      heading:       firstcompItem.heading      || '',
      subheading:    firstcompItem.subheading   || '',
      description:   firstcompItem.description  || '',
      button:        firstcompItem.button       || '',
      button1:       firstcompItem.button1      || '',
      onButtonClick: handleOpen,
      onSecondButtonClick: handleSecondButton,
      image:         firstcompItem.imgurl       || '',
      // SmartVitals hero uses a video – pass videoUrl so IntroSection can render it
      videoUrl:      firstcompItem.videoUrl     || '',
    },
  ];

  const secondcompArr      = attrs.second?.[0]?.secsub   || [];
  const thirdSection       = attrs.third?.[0]            || {};
  const fourthSection      = attrs.fourth?.[0]           || {};
  const fifthSection       = attrs.fifth?.[0]            || {};
  const sixthSection       = attrs.sixth?.[0]            || {};

  return (
    <>
      <LoginToast ref={loginToastRef} />
      <Toast ref={toastRef} position="top-right" />

      <section>
        {/* ── Animated background blobs (same as LungsCare) ── */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-[#42BA85]/8 to-transparent rounded-full blur-3xl animate-pulse" />
          <div
            className="absolute bottom-32 right-16 w-96 h-96 bg-gradient-to-tr from-[#F3F8FF]/10 to-transparent rounded-full blur-3xl animate-pulse"
            style={{ animationDelay: '1s' }}
          />
          <div
            className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-br from-[#42BA85]/6 to-transparent rounded-full blur-2xl animate-bounce"
            style={{ animationDelay: '2s', animationDuration: '3s' }}
          />
        </div>

        {/* SECTION 1 – Hero */}
        <IntroSection items={firstSectionItems} report_count={report_count} />

        {/* SECTION 2 – Stats / Status strip */}
        <section className="bg-[#D9F1E7]">
          <SecondSection items={secondcompArr} />
        </section>

        {/* SECTION 3 – "What You Can Expect?" image grid */}
        {Object.keys(thirdSection).length > 0 && (
          <ThirdSection data={thirdSection} mobileRow />
        )}

        {/* SECTION 4 – "How it Works?" steps */}
        {Object.keys(fourthSection).length > 0 && (
          <CheckHealthSteps data={fourthSection} onButtonClick={handleOpen} />
        )}

        {/* SECTION 5 – "Set Up Guidelines" / concerns */}
        {Object.keys(fifthSection).length > 0 && (
          <ConcernsSection data={fifthSection} />
        )}

        {/* SECTION 6 – "Check Our Other AI Tools" */}
        {Object.keys(sixthSection).length > 0 && (
          <CurebaySolutions data={sixthSection} onCardClick={handleCardClick} />
        )}
      </section>

      {/* ── Sticky CTA bar (same as old SmartVitals) ── */}
      
      {showFeedback && (
        <FeedbackModal service="Smart Vitals" patientId={userData?.code} onClose={closeFeedback} />
      )}
    </>
  );
};

export default SmartVitals;